import React, { useEffect, useMemo, useState } from "react";
import "./admin-product.css";
import { FaSearch } from "react-icons/fa";
import { MdOutlineAdd } from "react-icons/md";
import { HashLoader } from "react-spinners";
import { deleteProductForAdmin, getAllProductsForAdmin } from "../../api/product.api";
import { FaEye } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/modal/confirm-modal";
import { productTypeText } from "../../utils";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminProduct = () => {
  const [products, setProducts] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedProductType, setSelectedProductType] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const navigate = useNavigate();
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useUser();

  useEffect(() => {
    fetchProductData();
    document.title = "Admin - Sản phẩm";
  }, [currentPage, selectedStatus, selectedProductType, debouncedSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const summary = useMemo(() => {
    return products.reduce(
      (acc, product) => {
        if (product.product_status === "active") acc.active += 1;
        if (product.product_status === "inactive") acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );
  }, [products]);

  const fetchProductData = async (showLoader = true) => {
    try {
      if (showLoader) setIsFetching(true);
      const res = await getAllProductsForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim(),
        selectedStatus,
        selectedProductType
      );

      if (res.success) {
        setProducts(res.data.products || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalProducts(res.data.total || 0);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleDeleteProduct = async () => {
    if (isActionLoading) return;
    try {
      setIsActionLoading(true);
      setIsModalOpen(false);
      const res = await deleteProductForAdmin(selectedIdToDelete);

      if (res.success) {
        showToast("Xóa sản phẩm thành công", "success");
        await fetchProductData(false);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setIsActionLoading(false);
      setSelectedIdToDelete("");
    }
  };

  return (
    <div className="admin-product-page">
      <ToastNotification />
      <div className="admin-product-shell">
        <div className="admin-product-header">
          <div>
            <p className="admin-product-breadcrumb">Quản trị / Sản phẩm</p>
            <h1 className="admin-product-title">Danh sách sản phẩm</h1>
          </div>
          <div className="admin-product-kpi">
            <div className="admin-product-kpi-item">
              <span>Tổng sản phẩm</span>
              <strong>{totalProducts}</strong>
            </div>
            <div className="admin-product-kpi-item">
              <span>Trang hiện tại</span>
              <strong>{products.length}</strong>
            </div>
            <div className="admin-product-kpi-item">
              <span>Đang hoạt động</span>
              <strong>{summary.active}</strong>
            </div>
          </div>
        </div>

        <div className="admin-product-toolbar">
          <div className="admin-product-search-wrap">
            <FaSearch className="admin-product-search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm theo mã hoặc tên sản phẩm"
              className="admin-product-search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            className="admin-product-filter"
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Không hoạt động</option>
          </select>

          <select
            className="admin-product-filter"
            value={selectedProductType}
            onChange={(e) => {
              setSelectedProductType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tất cả loại sản phẩm</option>
            <option value="game_account">Tài khoản game</option>
            <option value="topup_package">Gói nạp</option>
            <option value="utility_account">Tài khoản tiện ích</option>
            <option value="gift_card">Gift Card</option>
          </select>

          {hasPermission(user?.role, "create:product") && (
            <button
              type="button"
              className="admin-product-add-button"
              onClick={() => navigate("/super-admin/products/add-product")}
            >
              <MdOutlineAdd className="admin-product-add-icon" />
              <span>Thêm sản phẩm</span>
            </button>
          )}
        </div>

        {isFetching && products.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="admin-product-table-wrap">
              <table className="admin-product-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Ảnh</th>
                    <th>Mã sản phẩm</th>
                    <th>Tên sản phẩm</th>
                    <th>Loại</th>
                    <th>Thông số</th>
                    <th>Trạng thái</th>
                    <th>Ngày tạo</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? (
                    products.map((product, index) => {
                      const displayStats =
                        product.product_type === "game_account"
                          ? `Giá: ${product.product_attributes?.price?.toLocaleString("vi-VN") || 0}đ | Acc: ${
                              product.product_attributes?.account?.length || 0
                            }`
                          : product.product_type === "topup_package"
                          ? `Số gói nạp: ${product.product_attributes?.packages?.length || 0}`
                          : "-";

                      return (
                        <tr key={product.productId}>
                          <td className="admin-product-cell-muted">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                          <td>
                            <img
                              className="admin-product-thumb"
                              src={product?.product_imgs?.[0] || require("./../../assets/images/test-img.jpg")}
                              alt={product?.product_name || "Product"}
                              loading="lazy"
                            />
                          </td>
                          <td className="admin-product-cell-code">{product.productId}</td>
                          <td>
                            <button
                              type="button"
                              className="admin-product-name-link"
                              onClick={() => navigate(`/super-admin/products/view/${product.productId}`)}
                            >
                              {product.product_name}
                            </button>
                          </td>
                          <td>{productTypeText(product.product_type)}</td>
                          <td className="admin-product-cell-muted">{displayStats}</td>
                          <td>
                            <span
                              className={`admin-product-status admin-product-status-${product.product_status}`}
                            >
                              {product.product_status === "active" ? "Hoạt động" : "Không hoạt động"}
                            </span>
                          </td>
                          <td className="admin-product-cell-muted">{new Date(product.createdAt).toLocaleString("vi-VN")}</td>
                          <td>
                            <div className="admin-product-actions">
                              <button
                                className="admin-product-action admin-product-action-view"
                                onClick={() => navigate(`/super-admin/products/view/${product.productId}`)}
                                title="Xem chi tiết"
                                disabled={isActionLoading}
                              >
                                <FaEye />
                              </button>
                              <button
                                className="admin-product-action admin-product-action-edit"
                                onClick={() => navigate(`/super-admin/products/edit/${product.productId}`)}
                                title="Chỉnh sửa"
                                disabled={isActionLoading}
                              >
                                <FaEdit />
                              </button>
                              {hasPermission(user?.role, "delete:product") && (
                                <button
                                  onClick={() => {
                                    setIsModalOpen(true);
                                    setSelectedIdToDelete(product.productId);
                                  }}
                                  className="admin-product-action admin-product-action-delete"
                                  disabled={isActionLoading}
                                  title="Xóa sản phẩm"
                                >
                                  <MdDelete />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9}>
                        <p className="admin-product-empty">Không có sản phẩm phù hợp bộ lọc.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        title="Xóa sản phẩm"
        message="Bạn có chắc chắn muốn xóa sản phẩm này?"
        onConfirm={handleDeleteProduct}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
